import cn from 'classnames'
import {FC, memo, useState} from 'react'

import Overlay from '@/components/common/overlay/Overlay'
import {EXPAND_TRANSITION} from '@/theme/cssTransitions'
import {
  FORM_CONTAINER_PADDING,
  FORM_CONTAINER_PADDING_MOBILE,
  FORM_SUMMARY_HEIGHT_MOBILE,
} from '@/theme/sizing'
import {theme} from '@/theme/theme'
import {Currency} from '@/utils/currency'
import {IpOfficePriceIncreaseAlertType} from '@/utils/registration-request/types'
import {MOBILE_SCREENSIZE} from '@/utils/styles/breakpoints'
import {analytics} from '@/utils/tracking/analytics'

import {FormSummaryMobileDropdown} from './FormSummaryMobileDropdown'

type FormSummaryMobileProps = {
  title: string
  price: number
  currency: Currency
  hasOverlay?: boolean
  hasDecimal?: boolean
  priceNote?: string
  ipOfficePriceIncreaseInfo?: IpOfficePriceIncreaseAlertType[]
}

export const FormSummaryMobile: FC<React.PropsWithChildren<FormSummaryMobileProps>> = memo(
  ({
    title,
    price,
    currency,
    hasOverlay = true,
    hasDecimal,
    priceNote,
    ipOfficePriceIncreaseInfo,
    children,
  }) => {
    const [isExpanded, setIsExpanded] = useState(false)

    const openBreakdown = () => {
      setIsExpanded(true)
      analytics.track('Mobile summary is open')
    }

    const closeBreakdown = () => {
      setIsExpanded(false)
      analytics.track('Mobile summary is closed')
    }

    const hasPriceIncreaseInfo = ipOfficePriceIncreaseInfo && ipOfficePriceIncreaseInfo.length > 0

    const zIndex = isExpanded ? (hasOverlay ? 0 : 1) : 3

    return (
      <>
        {hasOverlay && <Overlay close={() => closeBreakdown()} visible={isExpanded} />}
        <div className="summary">
          <FormSummaryMobileDropdown
            title={title}
            price={price}
            isExpanded={isExpanded}
            onClick={() => (isExpanded ? closeBreakdown() : openBreakdown())}
            currency={currency}
            hasDecimal={hasDecimal}
            priceNote={priceNote}
            ipOfficePriceIncreaseInfo={ipOfficePriceIncreaseInfo}
          />
          <div className={cn('breakdown', isExpanded && 'expanded')}>{children}</div>
        </div>

        <style jsx>{`
          .summary {
            position: relative;
            padding: 0 ${FORM_CONTAINER_PADDING};
            background-color: ${theme.palette.background.default};
            border-bottom: 1px solid ${theme.palette.gray[200]};
            margin: 0 -${FORM_CONTAINER_PADDING};
            z-index: 2;
          }
          .breakdown {
            position: absolute;
            background-color: ${theme.palette.background.default};
            width: 100%;
            top: 100%;
            left: 0;
            padding-left: ${FORM_CONTAINER_PADDING};
            padding-right: ${FORM_CONTAINER_PADDING};
            box-shadow: inset 0 -1px 0 ${theme.palette.gray[200]};
            max-height: 0;
            overflow-y: scroll;
            transition: all ${EXPAND_TRANSITION}ms ease-in-out;
          }
          .expanded {
            max-height: calc(
              100vh - ${FORM_SUMMARY_HEIGHT_MOBILE} - ${theme.spacing[9]} -
                ${hasPriceIncreaseInfo ? '120px' : '0px'}
            );
            max-height: calc(
              100svh - ${FORM_SUMMARY_HEIGHT_MOBILE} - ${theme.spacing[9]} -
                ${hasPriceIncreaseInfo ? '120px' : '0px'}
            );
            padding-top: ${theme.spacing[3]};
            padding-bottom: ${theme.spacing[5]};
            ${!hasOverlay ? `box-shadow: 0px 20px 32px ${theme.palette.gray[200]}` : ''};
          }
          ::-webkit-scrollbar-track {
            width: 4px;
            background-color: ${theme.palette.background.default};
          }
          ::-webkit-scrollbar {
            width: 4px;
            background-color: ${theme.palette.background.default};
          }
          ::-webkit-scrollbar-thumb {
            width: 4px;
            border-radius: 60px;
            background-color: ${theme.palette.gray[300]};
          }
          @media ${MOBILE_SCREENSIZE} {
            .summary {
              padding: 0 ${FORM_CONTAINER_PADDING_MOBILE};
              margin: 0 -${FORM_CONTAINER_PADDING_MOBILE};
            }
            .breakdown {
              padding-left: ${FORM_CONTAINER_PADDING_MOBILE};
              padding-right: ${FORM_CONTAINER_PADDING_MOBILE};
            }
            .expanded {
              padding-top: ${theme.spacing[2]};
              padding-bottom: ${theme.spacing[4]};
            }
          }
        `}</style>

        <style jsx global>{`
          #header {
            z-index: ${zIndex};
          }
        `}</style>
      </>
    )
  },
)
